<template>
    <div ref="scrollOverlayRef" class="scroll-overlay">
        <slot />
        <div class="gradient-container" :class="{isAtBottom, isAtTop, isScrollingY}"></div>
    </div>
</template>

<script setup lang="ts">
    /**
     * ⚠️ Important, this container is not the scroll container, but its parent
     * The default slot need to be the scroll container
     *
     * This is because the overlays are placed using absolute position,
     * so it needs to be placed by the parent element otherwise it would float with the content
     *
     * @usage
     *
     * ```vue
     *  <template>
     *     <ScrollOverflowIndicators>
     *        <!-- This is the scrollable container -->
     *        <div class="overflow-container"></div>
     *     </ScrollOverflowIndicators>
     *  </template>
     * ```
     */

    import {nextTick, onBeforeUnmount, onMounted, ref, useTemplateRef} from 'vue';
    import {useMutationObserver} from '@vueuse/core';
    import {useLogger} from '#imports';
    import {useScrollContainer} from '@malt/utils';

    defineSlots<{
        /** The scroll container */
        default(): HTMLElement;
    }>();

    const logger = useLogger();

    const scrollOverlayRef = useTemplateRef('scrollOverlayRef');
    const scrollContainerRef = ref<HTMLElement>();

    const {isAtBottom, isAtTop, isScrollingY, startListening, stopListening} = useScrollContainer({target: scrollContainerRef, immediate: false});

    const stickyElementsOffsetStart = ref(0);
    const stickyElementsOffsetEnd = ref(0);

    useMutationObserver(scrollOverlayRef, getStickyElementsHeight, {attributes: true});

    /** Some elements can be sticky on top or bottom of the scroll container
     * This collect their total height and add it to coords
     * */
    function getStickyElementsHeight() {
        try {
            const childrenElements = scrollOverlayRef.value?.children?.[0]?.querySelectorAll(':scope > *');

            if (childrenElements && childrenElements.length > 0) {
                const children = childrenElements?.entries() as unknown as Array<[number, Element]>;
                if ('reduce' in children && typeof children.reduce === 'function') {
                    const {end, start} = children?.reduce<{start: number; end: number}>(
                        (acc, [_, element]) => {
                            const style = getComputedStyle(element);
                            if (style.position === 'sticky') {
                                if (style.top != null && style.top !== 'none' && style.top !== 'auto') {
                                    acc.start += element.clientHeight;
                                    return acc;
                                } else if (style.bottom != null && style.bottom !== 'none' && style.bottom !== 'auto') {
                                    acc.end += element.clientHeight;
                                    return acc;
                                }
                            }
                            return acc;
                        },
                        {start: 0, end: 0},
                    ) ?? {start: 0, end: 0};
                    stickyElementsOffsetStart.value = start;
                    stickyElementsOffsetEnd.value = end;
                }
            } else {
                return 0;
            }
        } catch (e) {
            logger.warn(e);
        }
    }

    onMounted(async () => {
        const children = scrollOverlayRef.value?.children;
        // Compare to 2 because of the .gradient-container element
        if (children && children.length > 2) {
            logger.warn('[ScrollOverflowIndicators] Only one root element is allowed');
        } else if (children) {
            scrollContainerRef.value = children[0] as HTMLElement;
            startListening();
            await nextTick();
            getStickyElementsHeight();
        } else {
            logger.warn('[ScrollOverflowIndicators] No default slot found');
        }
    });

    onBeforeUnmount(() => {
        stopListening();
    });
</script>

<style lang="scss" scoped>
    .scroll-overlay {
        position: relative;

        .gradient-container {
            pointer-events: none;
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            height: 100%;
            z-index: 10000;

            &.isScrollingY {
                &::after,
                &::before {
                    content: '';
                    position: absolute;
                    pointer-events: none;
                    height: 50px;
                    left: 0;
                    width: 100%;
                    opacity: 0;
                    padding: 0 var(--joy-core-spacing-1);
                    transition: opacity var(--joy-transition-duration-long) ease;
                }

                &:before {
                    top: calc(v-bind(stickyElementsOffsetStart) * 1px);
                    background: linear-gradient(to top, rgba(255, 255, 255, 0), rgba(245, 245, 245, 1));
                }

                &:after {
                    bottom: calc(v-bind(stickyElementsOffsetEnd) * 1px);
                    background: linear-gradient(to bottom, rgba(255, 255, 255, 0), rgba(245, 245, 245, 1));
                }

                &:not(.isAtTop) {
                    &:before {
                        opacity: 1;
                    }
                }

                &:not(.isAtBottom) {
                    &:after {
                        opacity: 1;
                    }
                }
            }
        }
    }
</style>
